<template>
  <v-card :loading="isSearching">
    <v-card-title>
      <span class="text-h5">Add a converter</span>
    </v-card-title>
    <v-card-text>
      <v-form
        ref="addConverterForm"
        v-model="valid"
        lazy-validation
      >
        <v-container>
          <v-row>
            <v-col
              cols="12"
              sm="6"
              md="4"
            >
              <v-combobox
                v-model="newItem.code"
                :items="filteredConvertersCode"
                @change="selectedConverter"
                :search-input.sync="search"
                hide-no-data
                placeholder="Enter Code or Gradename"
                autofocus
              ></v-combobox>
            </v-col>
            <v-col
              cols="12"
              sm="6"
              md="4"
            >
              <v-text-field
                v-model="newItem.cost"
                label="cost"
                type="number"
                prefix="$"
              ></v-text-field>
            </v-col>
            <v-col
              cols="12"
              sm="6"
              md="4"
            >
              <v-text-field
                v-model="newItem.quantity"
                type="number"
                :min="0"
                :step="1"
                :rules="[rules.lessThanZero]"
                label="quantity"
              ></v-text-field>
            </v-col>
          </v-row>
        </v-container>
      </v-form>
      <p
        v-if="exceptionMessage"
        class="text-center red--text"
      >
        {{ exceptionMessage }}
      </p>
    </v-card-text>
    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn
        color="blue darken-1"
        text
        @click.stop.prevent="$emit('cancel', 'dialog', $refs.addConverterForm)"
      >
        Cancel
      </v-btn>
      <v-btn
        :disabled="currentlyAdding"
        color="blue darken-1"
        text
        @click="validateBeforeSave"
      >
        Save
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { ITEM } from "@/constants";
import { extractDataAxios } from "@/httpClient";
import {mapActions} from "@/store/modules/checkInSheet";

export default {
  async mounted() {
    this.allConverters = extractDataAxios(
      await window.axios.get(`/api/catalytic-converters/converter-search`, {
        params: { term: this.initSearch },
      })
    );
    this.selectedConverter();
  },
  props: {
    id: [String, Number],
    initSearch: {
      type: String,
      default: "",
    },
    converters: {
      type: Array,
      default: [],
    },
    userId: {
      type: [String, Number],
      default: null,
    },
  },
  watch: {
    search(val) {
      this.isSearching = true;
      if (val === "" || val === null) {
        this.isSearching = false;
        return;
      }
      if (this.searchToken) clearTimeout(this.searchToken);
      this.searchToken = setTimeout(() => {
        window.axios
          .get(`/api/catalytic-converters/converter-search`, {
            params: { term: val },
          })
          .then((response) => {
            this.allConverters = response.data.data;
          })
          .finally(() => {
            this.isSearching = false;
          });
      }, 1500);
    },
  },
  data() {
    return {
      isSearching: null,
      searchToken: null,
      valid: false,
      exceptionMessage: "",
      allConverters: [],
      currentlyAdding: false,
      newItem: Object.assign({}, ITEM, { code: this.initSearch }),
      rules: {
        lessThanZero: (v) => v > 0 || "please enter a value greater than 0",
      },
      search: this.initSearch,
    };
  },
  methods: {
    async selectedConverter() {
      const found = this.allConverters.find((val) => val.code === this.newItem.code);
      //if not found in converter list then just add it, no cost will be pre-filled
      if (!found) {
        this.newItem = { ...this.newItem, code: this.search, cost: 0 };
        return;
      }
      // if we did find it, let's fetch the converter again this time using single converter API call
      // reason for single converter fetch is to get price from stehling if exists
      try {
        const data = extractDataAxios(await window.axios.get(`/api/catalytic-converters/${found.id}/get-with-user-price`, { 
            params: { user_id: this.userId }
          }));
        if (data) {
          if (data.user_price) {
            this.newItem = { ...this.newItem, cost: data.user_price, quantity: 0 };
          } else {
            this.exceptionMessage = "Unable to fetch price, please try again!";
          }
        }
      } catch (e) {
        console.error(e);
        this.exceptionMessage = "Unable to fetch price, please try again!";
      }
    },
    async save() {
      try {
        const data = extractDataAxios(
          await window.axios.put(`/api/check-in-sheet/${this.id}/add-converter`, this.newItem)
        );
        this.newItem.id = data.id;
        this.converters.push(this.newItem);
        this.search = "";
        this.$emit("cancel");
        this.calculateTotals(this.id);
      } catch (err) {
        this.$emit("alertError", "error", "unable to add converter to sheet!");
      }
    },
    validateBeforeSave() {
      if (this.codesAlreadyInSheet.includes(this.newItem.code)) {
        this.exceptionMessage = "Duplicate Converter entry";
        return;
      }
      if (this.$refs.addConverterForm.validate()) {
        this.currentlyAdding = true;
        this.save();
      }
    },
    ...mapActions(['calculateTotals'])
  },
  computed: {
    codesAlreadyInSheet() {
      return this.converters.map((converter) => converter.code);
    },
    filteredConvertersCode() {
      return this.allConverters
        .filter((aConverter) => !this.codesAlreadyInSheet.includes(aConverter.code))
        .map((converter) => converter.code);
    },
  },
};
</script>
